import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import axios from '../../services/axios';
import Loading from '../../components/Loading';

import { Container } from '../../styles/layout/Container';
import { Table } from '../../styles/table/Table';
import { TableWrapper } from '../../styles/table/TableWrapper';

import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';

import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';

import {
   CardGrid,
   MiniCard,
   MiniCardTitle,
   MiniCardValue,
} from '../../styles/layout/CardGrid';

import { FormGrid } from '../../styles/layout/FormGrid';
import { ActionBar } from '../../styles/layout/ActionBar';
import { Field } from '../../styles/forms/Field';

import { EmptyState } from '../../styles/feedback/EmptyState';

const integerRegex = /^[0-9]+$/;

function toNumber(value) {
   if (value === null || value === undefined || value === '') return 0;
   const num = Number(String(value).replace(',', '.'));
   return Number.isFinite(num) ? num : 0;
}

function formatM3(value) {
   const num = toNumber(value);
   return num ? num.toFixed(3) : '0.000';
}

function formatGrams(value) {
   const num = toNumber(value);
   return Math.round(num).toString();
}

function isMissingNumber(value) {
   // null / undefined / '' => faltando
   if (value === null || value === undefined || value === '') return true;

   // strings tipo "0", "0.000", "0,000"
   const num = Number(String(value).replace(',', '.'));

   // NaN => faltando
   if (!Number.isFinite(num)) return true;

   // 0 ou negativo => faltando (se você quiser permitir 0, me avisa)
   return num <= 0;
}

async function fetchProductBySku(rawSku) {
   const sku = String(rawSku || '').replace(/\D/g, '');
   if (!sku) throw new Error('SKU inválido');

   // 1) Busca "rápida" pelo SKU (pode vir resumido)
   let searchData;
   try {
      const resSearch = await axios.get(`products/search/${sku}`);
      searchData = resSearch.data?.product || resSearch.data;

      // caso algum dia venha lista
      if (Array.isArray(searchData)) searchData = searchData[0];
   } catch (err) {
      if (err?.response?.status === 404) throw new Error('SKU não cadastrado');
      throw err;
   }

   if (!searchData?.id) {
      throw new Error('SKU não cadastrado');
   }

   // 2) Busca completa pelo ID (payload completo)
   let full;
   try {
      const resFull = await axios.get(`/products/${searchData.id}`);
      full = resFull.data?.product;
   } catch (err) {
      if (err?.response?.status === 404) throw new Error('SKU não cadastrado');
      throw err;
   }

   if (!full) throw new Error('SKU não cadastrado');

   // Normaliza o que a calculadora precisa
   return {
      id: full.id,
      prod_sku: full.prod_sku ?? sku,
      prod_name: full.prod_name ?? '',
      prod_weight: full.prod_weight ?? 0, // gramas
      volumetry: full.volumetry ?? 0, // m³
   };
}

export default function VolumetryCalculator() {
   const [isLoading, setIsLoading] = useState(false);

   // inputs
   const [sku, setSku] = useState('');
   const [qty, setQty] = useState(1);

   // produto carregado a partir do sku
   const [foundProduct, setFoundProduct] = useState(null);

   // lista
   const [items, setItems] = useState([]);

   const handleSearch = async () => {
      const cleanSku = String(sku || '').replace(/\D/g, '');

      if (!cleanSku) {
         toast.error('Informe um SKU válido.');
         return;
      }

      setIsLoading(true);
      try {
         const product = await fetchProductBySku(cleanSku);
         setFoundProduct(product);
      } catch (err) {
         if (
            err?.message === 'SKU não cadastrado' ||
            err?.response?.status === 404
         ) {
            toast.error('SKU não cadastrado');
         } else {
            toast.error('Erro ao buscar produto.');
         }
         setFoundProduct(null);
      }
      setIsLoading(false);
   };

   const handleAdd = () => {
      if (!foundProduct) {
         toast.error('Pesquise um produto antes de adicionar.');
         return;
      }

      const q = Number(qty) || 0;
      if (q < 1) {
         toast.error('Quantidade inválida.');
         return;
      }

      const missingWeight = isMissingNumber(foundProduct.prod_weight);
      const missingVol = isMissingNumber(foundProduct.volumetry);

      if (missingWeight && missingVol) {
         toast.error('Este produto não possui PESO e VOLUMETRIA cadastrados.');
         return;
      }
      if (missingWeight) {
         toast.error('Este produto não possui PESO cadastrado.');
         return;
      }
      if (missingVol) {
         toast.error('Este produto não possui VOLUMETRIA cadastrada.');
         return;
      }

      setItems((prev) => {
         // Se já existe esse SKU na lista, soma as quantidades
         const idx = prev.findIndex(
            (x) => String(x.prod_sku) === String(foundProduct.prod_sku)
         );
         if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], qty: Number(next[idx].qty) + q };
            return next;
         }

         return [
            ...prev,
            {
               prod_sku: foundProduct.prod_sku,
               prod_name: foundProduct.prod_name,
               prod_weight: foundProduct.prod_weight,
               volumetry: foundProduct.volumetry,
               qty: q,
            },
         ];
      });

      // mantém o produto encontrado pra facilitar vários adds,
      // mas reseta qty para 1 (padrão)
      setQty(1);
   };

   const handleRemove = (prod_sku) => {
      setItems((prev) =>
         prev.filter((x) => String(x.prod_sku) !== String(prod_sku))
      );
   };

   const handleClear = () => {
      const ok = window.confirm('Limpar toda a lista?');
      if (!ok) return;
      setItems([]);
   };

   const totals = useMemo(() => {
      const totalQty = items.reduce((acc, it) => acc + toNumber(it.qty), 0);
      const totalGrams = items.reduce(
         (acc, it) => acc + toNumber(it.qty) * toNumber(it.prod_weight),
         0
      );
      const totalM3 = items.reduce(
         (acc, it) => acc + toNumber(it.qty) * toNumber(it.volumetry),
         0
      );

      return {
         totalQty,
         totalGrams,
         totalKg: totalGrams / 1000,
         totalM3,
      };
   }, [items]);

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Calculadora de Volumetria</PageTitle>
               <PageSubtitle>
                  Some peso total e m³ total de um grupo de produtos
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <button
                  type="button"
                  onClick={handleClear}
                  disabled={items.length === 0}
               >
                  Limpar lista
               </button>
            </PageHeaderActions>
         </PageHeader>

         {/* Card: adicionar produto */}
         <Section>
            <SectionHeader>
               <SectionTitle>Adicionar produto</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid $cols={12} $colsMobile={12}>
                  {/* SKU + Pesquisar lado a lado no mobile */}
                  <Field $col={3} $colMobile={8}>
                     <label>SKU</label>
                     <input
                        type="text"
                        value={sku}
                        maxLength={9}
                        onChange={(e) => {
                           const v = e.target.value;
                           if (integerRegex.test(v) || v === '') {
                              setSku(v);
                              setFoundProduct(null);
                           }
                        }}
                        placeholder="Ex.: 1234567"
                     />
                  </Field>

                  <Field $col={2} $colMobile={4} $alignEnd>
                     <label>&nbsp;</label>
                     <button type="button" onClick={handleSearch}>
                        Pesquisar
                     </button>
                  </Field>

                  <Field $col={5} $colMobile={12}>
                     <label>Produto</label>
                     <input
                        type="text"
                        disabled
                        value={foundProduct?.prod_name || ''}
                     />
                  </Field>

                  <Field $col={2} $colMobile={12}>
                     <label>Quantidade</label>
                     <input
                        type="number"
                        min={1}
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                     />
                  </Field>

                  {/* leitura rápida do peso/vol do item encontrado */}
                  <Field $col={3} $colMobile={6}>
                     <label>Peso (g)</label>
                     <input
                        type="text"
                        disabled
                        value={
                           foundProduct
                              ? formatGrams(foundProduct.prod_weight)
                              : ''
                        }
                     />
                  </Field>

                  <Field $col={3} $colMobile={6}>
                     <label>Volumetria (m³)</label>
                     <input
                        type="text"
                        disabled
                        value={
                           foundProduct ? formatM3(foundProduct.volumetry) : ''
                        }
                     />
                  </Field>

                  <Field $col={6} $colMobile={12} $alignEnd>
                     <label>&nbsp;</label>
                     <ActionBar>
                        <button
                           type="button"
                           onClick={handleAdd}
                           disabled={!foundProduct}
                        >
                           Adicionar à lista
                        </button>
                     </ActionBar>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* Card: tabela */}
         <Section>
            <SectionHeader>
               <SectionTitle>Produtos adicionados</SectionTitle>
            </SectionHeader>
            <SectionBody>
               {items.length === 0 ? (
                  <EmptyState>
                     <h4>Nenhum produto na lista</h4>
                     <p>
                        Pesquise por SKU e adicione produtos para calcular os
                        totais.
                     </p>
                  </EmptyState>
               ) : (
                  <TableWrapper>
                     <Table>
                        <thead>
                           <tr>
                              <th>SKU</th>
                              <th>Produto</th>
                              <th style={{ textAlign: 'right' }}>Qtd</th>
                              <th style={{ textAlign: 'right' }}>Peso (g)</th>
                              <th style={{ textAlign: 'right' }}>Vol (m³)</th>
                              <th style={{ textAlign: 'right' }}>
                                 Peso total (g)
                              </th>
                              <th style={{ textAlign: 'right' }}>
                                 Vol total (m³)
                              </th>
                              <th>Ações</th>
                           </tr>
                        </thead>
                        <tbody>
                           {items.map((it) => {
                              const subtotalG =
                                 toNumber(it.qty) * toNumber(it.prod_weight);
                              const subtotalM3 =
                                 toNumber(it.qty) * toNumber(it.volumetry);

                              return (
                                 <tr key={String(it.prod_sku)}>
                                    <td>{it.prod_sku}</td>
                                    <td>{it.prod_name}</td>
                                    <td style={{ textAlign: 'right' }}>
                                       {it.qty}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                       {formatGrams(it.prod_weight)}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                       {formatM3(it.volumetry)}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                       {Math.round(subtotalG)}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                       {subtotalM3.toFixed(3)}
                                    </td>
                                    <td>
                                       <button
                                          type="button"
                                          onClick={() =>
                                             handleRemove(it.prod_sku)
                                          }
                                       >
                                          Remover
                                       </button>
                                    </td>
                                 </tr>
                              );
                           })}
                        </tbody>
                     </Table>
                  </TableWrapper>
               )}
            </SectionBody>
         </Section>

         {/* Card: resumo */}
         <Section>
            <SectionHeader>
               <SectionTitle>Resumo do grupo</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <CardGrid $span={12} $mdSpan={6} $lgSpan={4}>
                  <MiniCard>
                     <MiniCardTitle>Total de itens</MiniCardTitle>
                     <MiniCardValue style={{ textAlign: 'right' }}>
                        {totals.totalQty}
                     </MiniCardValue>
                  </MiniCard>

                  <MiniCard>
                     <MiniCardTitle>Peso total</MiniCardTitle>
                     <MiniCardValue style={{ textAlign: 'right' }}>
                        {totals.totalKg.toFixed(3)} kg
                     </MiniCardValue>
                  </MiniCard>

                  <MiniCard>
                     <MiniCardTitle>Volumetria total</MiniCardTitle>
                     <MiniCardValue style={{ textAlign: 'right' }}>
                        {totals.totalM3.toFixed(3)} m³
                     </MiniCardValue>
                  </MiniCard>
               </CardGrid>
            </SectionBody>
         </Section>
      </Container>
   );
}
